import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";
import { ArrowUpRight, ArrowDownRight, Minus, TrendingUp, ArrowUp, Star } from "lucide-react";

interface EventValuesProps {
  event: {
    previous: number;
    estimate: number;
    actual: number;
    change: number;
    unit?: string;
    impact?: string;
    score?: number;
  };
}

export const EventValues = ({ event }: EventValuesProps) => {
  const isMobile = useIsMobile();
  
  const formatNumber = (num: number | null | undefined) => {
    if (num === null || num === undefined) return 'N/A';
    return num.toFixed(num % 1 === 0 ? 0 : 2);
  };

  // Determine if the actual is better than estimate
  // Positive change is usually better, but context may vary per economic indicator
  const actualBetterThanEstimate = event.actual > event.estimate;
  const actualBetterThanPrevious = event.actual > event.previous;
  const actualEqualToEstimate = event.actual === event.estimate;
  const actualEqualToPrevious = event.actual === event.previous;

  const getChangeIcon = (isBetter: boolean, isEqual: boolean) => {
    if (isEqual) return <Minus className="h-4 w-4 text-gray-400" />;
    return isBetter 
      ? <ArrowUpRight className="h-4 w-4 text-green-500" /> 
      : <ArrowDownRight className="h-4 w-4 text-red-500" />;
  };

  const getChangeTextColor = (isBetter: boolean, isEqual: boolean) => {
    if (isEqual) return "text-gray-400";
    return isBetter ? "text-green-500" : "text-red-500";
  };

  // Add background highlight for the actual value when it differs from estimate
  const getActualHighlight = () => {
    if (actualEqualToEstimate) return "";
    return actualBetterThanEstimate 
      ? "bg-green-950/30 border-green-500/20" 
      : "bg-red-950/30 border-red-500/20";
  };

  const getImpactColor = (impact?: string) => {
    if (!impact) return "text-muted-foreground";
    switch (impact.toLowerCase()) {
      case 'high':
        return 'text-calendar-high';
      case 'medium':
        return 'text-calendar-medium';
      case 'low':
        return 'text-calendar-low';
      default:
        return 'text-muted-foreground';
    }
  };

  const getImpactBgColor = (impact?: string) => {
    if (!impact) return "bg-muted/10 border-muted/30";
    switch (impact.toLowerCase()) {
      case 'high':
        return 'bg-calendar-high/10 border-calendar-high/30';
      case 'medium':
        return 'bg-calendar-medium/10 border-calendar-medium/30';
      case 'low':
        return 'bg-calendar-low/10 border-calendar-low/30';
      default:
        return 'bg-muted/10 border-muted/30';
    }
  };

  const getImpactIcon = (impact?: string) => {
    if (!impact) return null;
    switch (impact.toLowerCase()) {
      case 'high':
        return <TrendingUp className="h-4 w-4" />;
      case 'medium':
        return <ArrowUp className="h-4 w-4" />;
      case 'low':
        return <Star className="h-4 w-4" />;
      default:
        return null;
    }
  };

  const getImpactDescription = (impact?: string) => {
    if (!impact) return "No impact information available";
    switch (impact.toLowerCase()) {
      case 'high':
        return 'High-impact economic event with significant market volatility potential';
      case 'medium':
        return 'Medium-impact economic event with moderate market volatility potential';
      case 'low':
        return 'Low-impact economic event with minimal market volatility potential';
      default:
        return 'Impact level unknown';
    }
  };

  return (
    <div className="space-y-6">
      {/* Impact information */}
      {event.impact && (
        <div className={cn(
          "p-4 rounded-lg border",
          getImpactBgColor(event.impact)
        )}>
          <div className="flex items-center gap-2 mb-1">
            <div className={cn(
              "flex items-center justify-center h-6 w-6 rounded-full",
              getImpactBgColor(event.impact)
            )}>
              {getImpactIcon(event.impact)}
            </div>
            <h3 className={cn(
              "text-sm font-medium",
              getImpactColor(event.impact)
            )}>
              {event.impact} Impact Event
              {event.score && <span className="ml-2 font-mono">({event.score.toFixed(1)})</span>}
            </h3>
          </div>
          <p className="text-xs text-muted-foreground">
            {getImpactDescription(event.impact)}
          </p>
        </div>
      )}
      
      <h3 className="text-sm font-medium text-muted-foreground">Event Data</h3>
      
      <div className={cn(
        "grid gap-4",
        isMobile ? "grid-cols-1" : "grid-cols-2"
      )}>
        {/* Previous value */}
        <div className="flex flex-col gap-1 p-3 rounded-lg border border-border/50 bg-card/30">
          <span className="text-xs text-muted-foreground">Previous</span>
          <div className="flex items-center justify-between">
            <span className="font-mono text-base">{formatNumber(event.previous)}</span>
            {event.unit && <span className="text-xs text-muted-foreground">{event.unit}</span>}
          </div>
        </div>

        {/* Estimate value */}
        <div className="flex flex-col gap-1 p-3 rounded-lg border border-border/50 bg-card/30">
          <span className="text-xs text-muted-foreground">Estimate</span>
          <div className="flex items-center justify-between">
            <span className="font-mono text-base">{formatNumber(event.estimate)}</span>
            {event.unit && <span className="text-xs text-muted-foreground">{event.unit}</span>}
          </div>
        </div>

        {/* Actual value - highlighted */}
        <div className={cn(
          "flex flex-col gap-1 p-3 rounded-lg border",
          "transition-colors duration-300",
          getActualHighlight() || "border-border/50 bg-card/30",
          isMobile ? "" : "col-span-2"
        )}>
          <span className="text-xs text-muted-foreground">Actual</span>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="font-mono text-lg font-medium">{formatNumber(event.actual)}</span>
              {event.unit && <span className="text-xs text-muted-foreground">{event.unit}</span>}
            </div>
            
            <div className="flex items-center gap-4">
              {/* Comparison to estimate */}
              <div className="flex items-center gap-1">
                <span className="text-xs text-muted-foreground">vs Est:</span>
                <div className="flex items-center gap-1">
                  {getChangeIcon(actualBetterThanEstimate, actualEqualToEstimate)}
                  <span className={cn(
                    "font-mono text-sm",
                    getChangeTextColor(actualBetterThanEstimate, actualEqualToEstimate)
                  )}>
                    {actualEqualToEstimate ? "=" : (actualBetterThanEstimate ? "+" : "-")}
                  </span>
                </div>
              </div>
              
              {/* Comparison to previous */}
              <div className="flex items-center gap-1">
                <span className="text-xs text-muted-foreground">vs Prev:</span>
                <div className="flex items-center gap-1">
                  {getChangeIcon(actualBetterThanPrevious, actualEqualToPrevious)}
                  <span className={cn(
                    "font-mono text-sm",
                    getChangeTextColor(actualBetterThanPrevious, actualEqualToPrevious)
                  )}>
                    {event.change > 0 ? "+" : (event.change < 0 ? "" : "=")}
                    {event.change !== 0 ? formatNumber(event.change) : ""}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}; 